import { get } from 'lodash';
import { DateFromJSDate } from '../../helpers/Date';
import { normalizeMedicineIcd } from '../../helpers/medicineIcds';
import { MedicalReferralDTO, Specialty } from '../../interfaces/MedicalReferral';
import {
  PosologiaResponseType,
  Prescription,
  PRESCRIPTION_STATUS,
  PrescriptionByAudioResponseType,
} from '../../interfaces/prescription';
import { workspace } from '../../shared/api';
import { ApacBpaApiPayload } from '../apac-bpa/Forms/helpers';

export const PRESCRIPTION_EMISSION_TIMEOUT = 55000;

export const PrescriptionService = {
  async fetchPrescription(
    prescriptionReference: number | string
  ): Promise<{ prescription: Prescription; tokenExpirationData?: TokenExpirationData }> {
    const params = {
      referencia: prescriptionReference,
    };

    const { data, headers } = await workspace.get<Prescription>(`/prescricao/consultareferencia`, { params });

    const tokenExpiresAt = get(headers, 'x-jwt-token-expires-at', null);
    const tokenExpiresTimezone = get(headers, 'x-jwt-token-expires-timezone', null);

    return {
      prescription: {
        ...data,
        Itens: data.Itens.map((medicine) => ({
          ...medicine,
          Ordem: medicine.Ordem ? medicine.Ordem : null,
          DataAdicao: DateFromJSDate(new Date()),
          medicineIcd: normalizeMedicineIcd(medicine.medicineIcd),
        })),
        ItensManuais: data.ItensManuais.map((manualMedicine) => ({
          ...manualMedicine,
          Manual: true,
          Ordem: manualMedicine.Ordem ? manualMedicine.Ordem : null,
          DataAdicao: DateFromJSDate(new Date()),
        })),
        Exames: data.Exames.map((exam) => ({
          ...exam,
          tipoFormulario: exam.tipoFormulario
            ? exam.tipoFormulario
            : { Chave: null, Nome: 'Nenhum', idTipoExameFormulario: 0 },
        })),
        idPrescritor: data.Prescritor.idPrescritor,
      },
      ...(tokenExpiresAt &&
        tokenExpiresTimezone && {
          tokenExpirationData: {
            expiresAt: tokenExpiresAt,
            timeZone: tokenExpiresTimezone,
          },
        }),
    };
  },

  async updatePrescription(data: UpdatePrescriptionPayload): Promise<Prescription> {
    const payload: Partial<UpdatePrescriptionEndpointPayload> = {
      printingEnabled: data.ImpressaoHabilitada,
      patient: {
        email: data.Emails[0],
        phone: data.Telefones[0],
        document: data.patientData?.document || '',
      },
      status: data.status,
      medicinesObservations: data.Comentario,
      showDateInPdf: data.showDateInPdf,
      emittedTo: data.emittedTo ? DateFromJSDate(data.emittedTo) : null,
      issueMedicinesInSeparateTabs: data.issueMedicinesInSeparateTabs,
      hasDigitalCertificates: data.hasDigitalCertificates,
      skipDefaultRqe: data.skipDefaultRqe,
    };

    if (data.patientData && data.patientData.address && payload.patient) {
      const addressPayload = data.patientData.address;
      payload.patient.address = addressPayload.address;
      payload.patient.address2 = addressPayload.complement;
      payload.patient.city = addressPayload.city;
      payload.patient.state = addressPayload.federalUnit;
      payload.patient.neighborhood = addressPayload.neighborhood;
      payload.patient.houseNumber = `${addressPayload.number}`;
      payload.patient.zipCode = addressPayload.postalCode;
    }

    if (data.patientData && data.patientData.gender && payload.patient) {
      payload.patient.gender = data.patientData.gender;
    }

    if (data.patientData && data.patientData.birthDate && payload.patient) {
      payload.patient.birthDate = data.patientData.birthDate;
    }

    if (data.patientData && data.patientData.icd10structured && payload.patient) {
      payload.patient.icd10structured = data.patientData.icd10structured;
    }

    if (data.patientData && data.patientData.passport && payload.patient) {
      payload.patient.passport = data.patientData.passport;
    }

    if (data.signData !== null) {
      payload.signData = data.signData;
    }

    if (data.establishment) {
      payload.establishment = data.establishment;
    }

    const response = await workspace.put(`/prescricao/${data.idPrescricao}`, payload, {
      timeout: PRESCRIPTION_EMISSION_TIMEOUT,
    });
    return response.data;
  },

  async fetchPrescriptionById(prescriptionId: number): Promise<Prescription> {
    const params = {
      idPrescricao: prescriptionId,
    };

    const { data } = await workspace.get<Prescription>('/prescricao/consulta', { params });
    return {
      ...data,
      Itens: (data.Itens || []).map((medicine) => ({
        ...medicine,
        medicineIcd: normalizeMedicineIcd(medicine.medicineIcd),
      })),
      ItensManuais: (data.ItensManuais || []).map((manualMedicine) => ({
        ...manualMedicine,
        Manual: true,
      })),
    };
  },

  async localSignPrescription(
    prescriptionId: number,
    localCertificateData: LocalCertificateData
  ): Promise<LocalSignResponse> {
    const payload = {
      certificateInfo: {
        certDocument: localCertificateData.ownerDocument,
        certificateContent: localCertificateData.content,
        certificateThumbprint: localCertificateData.thumbprint,
        certName: localCertificateData.name,
        alwaysUse: localCertificateData.alwaysUse,
      },
    };

    const { data } = await workspace.post(`/prescricao/${prescriptionId}/localSign`, payload);
    return data;
  },
  async validatePrescription(prescriptionId: number): Promise<PrescriptionValidation> {
    const { data } = await workspace.get(`/prescricao/${prescriptionId}/validacao`);
    return data;
  },
  async cancelPrescription(prescriptionId: number, reason?: string) {
    const { data } = await workspace.delete(`/prescricao/${prescriptionId}`, {
      data: { motivoCancelamento: reason || '' },
    });
    return data;
  },

  async updatePrescriptionShowByActivePrinciple(prescriptionId: number, showByActivePrinciple: boolean) {
    const body = {
      showActivePrinciple: showByActivePrinciple,
    };

    const { data } = await workspace.put(`/prescricao/${prescriptionId}/principioativo`, body);
    return data;
  },

  async getSMRForm(prescriptionId: number): Promise<SMRForm> {
    const { data } = await workspace.get(`/prescricao/lme/${prescriptionId}`);

    return data;
  },

  async getBypass(prescriberId: number): Promise<boolean> {
    const { data } = await workspace.get(`/bypass/${prescriberId}`);

    return data.isActive;
  },
  async updateSMRForm(prescriptionId: number, SMRForm: SMRForm): Promise<SMRForm> {
    const { data } = await workspace.put(`/prescricao/lme/${prescriptionId}`, SMRForm);
    return data;
  },
  async fetchWhatsAppConsent(prescriberId: number): Promise<WhatsAppConsent> {
    const { data } = await workspace.get(`medico/${prescriberId}/consentimento?TipoConsentimento=WhatsApp_Web`);
    return data;
  },
  async updateWhatsAppConsent(prescriberId: number, prescriptionId: number, consentText: string) {
    const body = {
      IdPrescricao: prescriptionId,
      TipoConsentimento: 'WhatsApp_Web',
      TextoConsentimento: consentText,
    };

    await workspace.post(`/medico/${prescriberId}/consentimento`, body);
  },
  async fetchConsent(prescriberId: number, consentType: string): Promise<any> {
    const { data } = await workspace.get(`medico/${prescriberId}/consentimento?TipoConsentimento=${consentType}`);
    return data;
  },
  async updateConsent(
    prescriberId: number,
    prescriptionId: number,
    consentText: string,
    termoAceite: boolean,
    tipoConsentimento: string,
    email: string
  ) {
    const body = {
      IdPrescricao: prescriptionId,
      TipoConsentimento: tipoConsentimento,
      TextoConsentimento: consentText,
      TermoAceite: termoAceite,
      Contato: email,
    };

    await workspace.post(`/medico/${prescriberId}/consentimento`, body);
  },
  async refuseConsent(prescriberId: number, consentType: string): Promise<void> {
    await workspace.post(`/medico/${prescriberId}/consentimento/recusar/${consentType}`);
  },
  async resendPrescription(data: { prescriptionId: number; phoneNumber: string; email: string }) {
    const body = {
      TelefoneCelular: data.phoneNumber.replace(/^\+/, ''),
      Email: data.email,
    };

    await workspace.post(`/prescricao/${data.prescriptionId}/reenviarprescricao`, body);
  },
  async fetchMedicalReferralSpecialty(searchBy: string): Promise<Specialty[]> {
    const { data } = await workspace.get(`/medico/especialidade?BuscarPor=${searchBy}&Encaminhamento=true`);
    return data.data;
  },
  async createMedicalReferral(prescriptionId: number, payload: MedicalReferralDTO): Promise<void> {
    await workspace.post(`/prescricao/${prescriptionId}/encaminhamento`, payload);
  },
  async updateMedicalReferral(prescriptionId: number, payload: MedicalReferralDTO): Promise<void> {
    await workspace.put(`/prescricao/${prescriptionId}/encaminhamento/${payload.idEncaminhamentoPrescricao}`, payload);
  },
  async removeMedicalReferral(prescriptionId: number, medicalReferralId: number): Promise<void> {
    await workspace.delete(`/prescricao/${prescriptionId}/encaminhamento/${medicalReferralId}`);
  },
  async updatePrescriptionObservation(prescriptionId: number, observation: string | null): Promise<void> {
    await workspace.put(`/prescricao/${prescriptionId}/comentario`, {
      Comentario: observation,
    });
  },
  async changeSelectedRqe(prescriptionId: number, selectRqeParams: SelectRqeParams): Promise<void> {
    await workspace.put(`/prescricao/${prescriptionId}/rqe`, selectRqeParams);
  },
  async printDocuments(prescriptionId: number): Promise<PrescriptionDocuments> {
    const { data } = await workspace.get(`/prescricao/${prescriptionId}/imprimir`);
    return data;
  },
  async getInteractionsToJustify(prescriptionId: number): Promise<PrescriptionInteractionJustifyDetail[]> {
    const { data } = await workspace.get(`/prescricao/${prescriptionId}/interacao`);
    return data.data;
  },
  async justifyInteraction(
    prescriptionId: number,
    interactionId: number,
    justification: string
  ): Promise<PrescriptionInteractionJustifyDetail[]> {
    const { data } = await workspace.put(`/prescricao/${prescriptionId}/interacao/${interactionId}`, {
      justification,
    });
    return data.data;
  },
  async getPrescriptionStatus(prescriptionId: number): Promise<PrescriptionStatus> {
    const { data } = await workspace.get(`/prescricao/status?idPrescricao=${prescriptionId}`);

    return data;
  },

  async addItemsFromPrescription(
    currentPrescriptionId: number,
    originPrescriptionId: number,
    removeItems: boolean,
    addLmeFromOriginPrescription: boolean
  ): Promise<void> {
    const { data } = await workspace.post(`/prescricao/${currentPrescriptionId}/adicionar-itens-prescricao`, {
      idPrescricaoOrigem: originPrescriptionId,
      removerItens: removeItems,
      addLmeFromOriginPrescription: addLmeFromOriginPrescription,
    });

    return data;
  },
  async getApacBpaForm(prescriptionId: number): Promise<ApacBpaApiPayload> {
    const { data } = await workspace.get(`/prescricao/${prescriptionId}/exame/formulario`);
    return data;
  },
  async updateApacBpaForm(prescriptionId: number, payload: ApacBpaApiPayload): Promise<ApacBpaApiPayload> {
    const { data } = await workspace.put(`/prescricao/${prescriptionId}/exame/formulario`, payload);
    return data;
  },
  async verifyIfPrescriptionIsDuplicated(prescriptionId: number): Promise<{
    Duplicado: boolean;
    IDPrescricaoDuplicada: number | null;
  }> {
    const { data } = await workspace.get(`/prescricao/${prescriptionId}/validar`);

    return data;
  },

  async getPosologyByText(
    idMedico: number,
    posologyText: string,
    idMedicamento: number,
    usoContinuo: boolean
  ): Promise<PosologiaResponseType> {
    const { data } = await workspace.post('/prescricao/posologia', {
      IdMedico: idMedico,
      posologia: posologyText,
      idMedicamento,
      usoContinuo,
    });
    return data;
  },

  async createPrescriptionByAudio(formData: FormData): Promise<PrescriptionByAudioResponseType> {
    const { data } = await workspace.post('/prescription-by-audio/transcribe', formData);
    return data;
  },

  async sendUserSignUp(mobilePhoneNumber: string, doctorId: number): Promise<{ timeout: number }> {
    const { data } = await workspace.post('/partner-campaign/easy-sign-up/send', {
      mobilePhoneNumber,
      doctorId,
    });
    return data;
  },
};

export interface UserSignUpResponse {
  enabled: boolean;
  data: {
    timeout: number;
    url: string;
  };
}

export enum PRESCRIPTION_DOCUMENT_TYPE {
  MEDICINE = 'Medicamentos',
  EXAM = 'Exames',
  MEDICAL_STATEMENT = 'Atestado',
  SMR = 'Medicamentos LME',
  REFERRAL = 'Encaminhamentos',
  MEDICAL_REPORT = 'Relatorios',
}

export interface DocumentItem {
  signature: string;
  url: string;
  type: string;
  pdfBase64: string | null;
}
export interface PrescriptionDocuments {
  documents: DocumentItem[];
}
export interface SelectRqeParams {
  idMedico: number;
  RQESelecionado: {
    idMedicoRqe: number;
    textoSimples: string;
    textoCompleto: string;
    ativo: boolean;
  } | null;
}

export interface WhatsAppConsent {
  TermoAceite: boolean;
  TipoConsentimento: string;
  DataAceite: Date;
}

export interface SMRForm {
  pacienteLme: PacienteLme;
  medicoLme: MedicoLme;
  estabelecimentoLme: EstabelecimentoLme;
  prescricaoCidLme: PrescricaoCidLme[];
}

export interface MedicoLme {
  idPrescricaoMedicoLme: number;
  idPrescricao: number;
  cns: string;
  nome: string;
}

export interface EstabelecimentoLme {
  idPrescricaoEstabelecimentoLme: number;
  idPrescricao: number;
  cnes: string;
  nome: string;
}

export interface PrescricaoCidLme {
  idPrescricaoCidLme: number;
  idPrescricao: number;
  anamnese: string | null;
  tratamentoAnterior: boolean;
  descricaoTratamento: string | null;
  cid: ICD;
  medicamentosLme: MedicamentosLme[];
  termosResponsabilidadeDisponiveis: {
    id: number;
    url: string;
    name: string;
  }[];
  termosResponsabilidadeSelecionados: {
    id: number;
  }[];
}

export interface MedicamentosLme {
  idPrescricaoMedicamentoLme: number;
  idPrescricaoLmeIcd: number;
  label: string;
  idPrescricaoMedicamento: number;
  MedicamentosUnidadeLme: MedicamentosUnidadeLme[];
  paranaSemDor: boolean;
}

export interface ICD {
  id: number;
  subCategoria: string;
  descricao: string;
}

export interface MedicamentosUnidadeLme {
  idPrescricaoMedicamentoUnidadeLme?: number;
  idPrescricaoMedicamentoLme?: number;
  mes: number;
  unidade: number;
}

export interface PacienteLme {
  idPrescricaoPacienteLme: number;
  idPrescricao: number;
  Nome: string;
  NomeSocial?: string;
  NomeMae: string;
  Peso: number | null;
  Altura: number | null;
  Incapaz: boolean;
  NomeResponsavel: string;
  Etnia: string;
  EtniaIndigena: string;
  Telefone?: string;
  TipoDocumento: string;
  Documento?: string;
  Email?: string;
  PreenchimentoManual: boolean;
}

export interface PrescriptionValidation {
  Paciente: {
    CID: boolean;
    DataNascimento: boolean;
    Endereco: {
      EnderecosContemNumero: number;
      Bairro: boolean;
      CEP: boolean;
      Cidade: boolean;
      Endereco1: boolean;
      Endereco2: boolean;
      Numero: boolean;
      Estado: boolean;
    };
    Sexo: boolean;
  };
  Estabelecimento: {
    Endereco: boolean;
  };
}

export interface UpdatePrescriptionPayload {
  status: PRESCRIPTION_STATUS;
  idPrescricao: number;
  Comentario: string;
  Emails: Array<string>;
  Telefones: Array<string>;
  ComentarioExame: string;
  ImpressaoHabilitada: boolean;
  // signData?: SignData | null;
  // @todo consertar isso daqui
  signData?: any;
  patientData?: {
    gender?: string;
    address?: {
      address: string;
      city: string;
      federalUnit: string;
      number: string;
      neighborhood: string;
      postalCode: string;
      complement: string;
    };
    birthDate?: string;
    icd10structured?: {
      code: string;
      idICD10: number;
      description: string;
    }[];
    document: string;
    passport?: {
      number: string;
      country: string;
      emissionCountry: string;
    };
  };
  showDateInPdf: boolean;
  emittedTo: Date | null;
  establishment?: {
    address1: string;
    address2: string;
    neighborhood: string;
    city: string;
    state: string;
    postalCode: string;
  };
  issueMedicinesInSeparateTabs: boolean;
  hasDigitalCertificates: boolean;
  skipDefaultRqe?: boolean;
}

interface UpdatePrescriptionEndpointPayload {
  medicinesObservations: string;
  examsObservations: string;
  sendPrescriptionSms: boolean;
  printingEnabled: boolean;
  patient: {
    email?: string;
    phone: string;
    document: string;
    name?: string;
    address?: string;
    address2?: string;
    zipCode?: string;
    birthDate?: string; // 2020-12-13T12:46:58.451Z
    houseNumber?: string;
    state?: string;
    neighborhood?: string;
    city?: string;
    gender?: string;
    height?: string;
    weigth?: string;
    diagnoses?: string[];
    alergies?: string[];
    lactating?: boolean;
    smoker?: boolean;
    pregnant?: boolean;
    icd10structured?: {
      code: string;
      idICD10: number;
      description: string;
    }[];
    passport?: {
      number: string;
      country: string;
      emissionCountry: string;
    };
  };
  signData: SignData | null;
  emittedTo: string | Date | null;
  prescriptionDocuments: PrescriptionDocument[];
  status: PRESCRIPTION_STATUS;
  showDateInPdf: boolean;
  establishment?: {
    address1: string;
    address2: string;
    neighborhood: string;
    city: string;
    state: string;
    postalCode: string;
  };
  issueMedicinesInSeparateTabs: boolean;
  hasDigitalCertificates: boolean;
  skipDefaultRqe?: boolean;
}

interface Token {
  token: string;
  documentType: string;
}

interface PrescriptionDocument {
  url: string;
  signed: boolean;
  emitter: string;
  forceDownload: boolean;
}

interface SignData {
  pin: string;
  sessionToken: string;
  useSession: boolean;
  certDocument: boolean;
  provider: string;
  tokens: Token[];
}

interface LocalCertificateData {
  ownerDocument: string | null;
  content: string;
  thumbprint: string;
  name: string;
  alwaysUse: boolean;
}

interface LocalSignResponse {
  medicamentoToken?: {
    Token: string;
  };
  exameToken?: {
    Token: string;
  };
  atestadoToken?: {
    Token: string;
  };
  encaminhamentoToken?: {
    Token: string;
  };
  relatorioToken?: {
    Token: string;
  };
  lmeToken?: {
    Token: string;
  };
}

export type INTERACTION_ACTION_TYPES = 'N/A' | 'Justificativa' | 'Notificação';
export type INTERACTION_TYPES =
  | 'PREGNANCY'
  | 'LACTATION'
  | 'FOOD'
  | 'LAB'
  | 'ETHANOL'
  | 'TOBACCO'
  | 'INGREDIENT_DUPLICATION'
  | 'DRUG'
  | 'ALLERGY';

export interface PrescriptionInteractionJustifyDetail {
  prescriptionInteractionJustificationId: number;
  type: INTERACTION_TYPES;
  severity: string;
  interactionText: string;
  firstMedicineName: string;
  secondMedicineName: string | null;
  requiredAction: INTERACTION_ACTION_TYPES;
  justification: string | null;
  prescriptionId: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface SeverityDetails {
  idPrimeiroMedicamento: number | null;
  idSegundomedicamento: number | null;
  severidade: string;
  tipo: string;
  textoAviso: string;
}

export interface PrescriptionStatus {
  idPrescricaoStatus: number;
  Status: string;
  DataModificacao: Date;
}

export interface TokenExpirationData {
  expiresAt: string;
  timeZone: string;
}
